import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage, permLabel } from '../api/client';
import type { AclEntry, BucketInfo, CreateAclResult, PublicGrant } from '../api/types';
import { Perm } from '../api/types';
import { AclEntryDetails } from '../components/AclEntryDetails';
import { config } from '../config';

const PERM_KEYS = ['READ', 'WRITE', 'LIST', 'DELETE'] as const;
const PUBLIC_PERM_KEYS = ['READ', 'LIST'] as const;

type FormType = 'apikey' | 'oauth' | 'tenant' | 'public';

const inputCls =
  'w-full rounded border border-gray-300 px-2.5 py-1.5 text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy';
const labelCls = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500';

function CreateForm({
  buckets,
  onClose,
  onCreated,
}: {
  buckets: BucketInfo[];
  onClose: () => void;
  onCreated: (result: CreateAclResult) => void;
}) {
  const [type, setType] = useState<FormType>('apikey');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [bucket, setBucket] = useState(buckets[0]?.logicalName ?? '');
  const [prefix, setPrefix] = useState('');
  const [perms, setPerms] = useState<number>(Perm.READ | Perm.LIST);
  const [expires, setExpires] = useState('');
  // Optional service-token constraints for oauth/tenant entries (e.g. a client-
  // credentials service authenticating with its own app's audience/issuer).
  const [appId, setAppId] = useState('');
  const [audience, setAudience] = useState('');
  const [issuer, setIssuer] = useState('');

  const isPublic = type === 'public';
  const isOauth = type === 'oauth' || type === 'tenant';
  const permKeys = isPublic ? PUBLIC_PERM_KEYS : PERM_KEYS;

  const create = useMutation({
    mutationFn: () => {
      if (isPublic) {
        return api<CreateAclResult>('/api/admin/public', {
          method: 'POST',
          body: JSON.stringify({ bucket, pathPrefix: prefix, perms: perms & (Perm.READ | Perm.LIST) }),
        });
      }
      const svc = {
        ...(appId.trim() ? { appId: appId.trim() } : {}),
        ...(audience.trim() ? { audience: audience.trim() } : {}),
        ...(issuer.trim() ? { issuer: issuer.trim() } : {}),
      };
      const oauth =
        type === 'oauth' ? { email, ...svc } : type === 'tenant' ? { tenant: config.tenantId, ...svc } : undefined;
      return api<CreateAclResult>('/api/admin/acl', {
        method: 'POST',
        body: JSON.stringify({
          name,
          principalType: type === 'tenant' ? 'oauth' : type,
          oauth,
          ...(expires ? { expiresUtc: new Date(expires).toISOString() } : {}),
          grants: bucket ? [{ bucket, pathPrefix: prefix, perms }] : [],
        }),
      });
    },
    onSuccess: (result) => onCreated((result ?? {}) as CreateAclResult),
  });

  const onChangeType = (t: FormType) => {
    setType(t);
    if (t === 'public') {
      // Anonymous is read-only; drop WRITE/DELETE, default to READ|LIST.
      setPerms((p) => (p & (Perm.READ | Perm.LIST)) || Perm.READ | Perm.LIST);
    }
  };

  const togglePerm = (bit: number) => setPerms((p) => (p & bit ? p & ~bit : p | bit));

  const canCreate = isPublic
    ? !!bucket && (perms & (Perm.READ | Perm.LIST)) !== 0
    : !!name.trim() && (type !== 'oauth' || !!email.trim());

  return (
    <div className="mb-4 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <h2 className="mb-4 text-base font-semibold text-navy">New access entry</h2>

      <div className="space-y-4">
        <div>
          <label className={labelCls}>Grant access to</label>
          <select className={inputCls} value={type} onChange={(e) => onChangeType(e.target.value as FormType)}>
            <option value="apikey">API key — a machine client</option>
            <option value="oauth">Entra user — a specific person</option>
            <option value="tenant">Entra tenant — everyone in the organization</option>
            <option value="public">Public — anyone, no credentials</option>
          </select>
        </div>

        {!isPublic ? (
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className={labelCls}>Name</label>
              <input
                className={inputCls}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={type === 'apikey' ? 'e.g. odincli-ci' : type === 'tenant' ? 'e.g. odincli users' : 'e.g. Jane Doe'}
              />
            </div>
            {type === 'oauth' ? (
              <div>
                <label className={labelCls}>User email</label>
                <input
                  className={inputCls}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@pandoscape.com"
                />
              </div>
            ) : null}
            <div>
              <label className={labelCls}>Expires (optional)</label>
              <input type="datetime-local" className={inputCls} value={expires} onChange={(e) => setExpires(e.target.value)} />
              <p className="mt-1 text-xs text-gray-500">Blank = never. The entry stops authorizing at this time.</p>
            </div>
          </div>
        ) : null}

        {isOauth ? (
          <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Service token (optional)</div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className={labelCls}>App ID</label>
                <input
                  className={inputCls}
                  value={appId}
                  onChange={(e) => setAppId(e.target.value)}
                  placeholder="(any app)"
                  spellCheck={false}
                />
              </div>
              <div>
                <label className={labelCls}>Custom audience</label>
                <input
                  className={`${inputCls} font-mono`}
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder="(FABS default)"
                  spellCheck={false}
                />
              </div>
              <div>
                <label className={labelCls}>Custom issuer</label>
                <input
                  className={`${inputCls} font-mono`}
                  value={issuer}
                  onChange={(e) => setIssuer(e.target.value)}
                  placeholder="(FABS default)"
                  spellCheck={false}
                />
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              For a service that presents its own client-credentials token (e.g. the data service): restrict to an
              app id, and set the token's audience/issuer when they differ from FABS's own. Blank = FABS default.
            </p>
          </div>
        ) : null}

        <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Bucket</label>
              <select className={inputCls} value={bucket} onChange={(e) => setBucket(e.target.value)}>
                {buckets.map((b) => (
                  <option key={b.logicalName} value={b.logicalName}>{b.logicalName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Path pattern</label>
              <input
                className={`${inputCls} font-mono`}
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
                placeholder="(whole bucket)"
                spellCheck={false}
              />
              <p className="mt-1 text-xs text-gray-500">
                Blank = whole bucket. Patterns are explicit:{' '}
                <span className="font-mono">sql/**</span> = everything under{' '}
                <span className="font-mono">sql</span>, <span className="font-mono">sql/*</span> = its
                direct files only, <span className="font-mono">sql</span> = the folder + direct files,{' '}
                <span className="font-mono">sql/**/*.bak</span> = .bak files at any depth,{' '}
                <span className="font-mono">sql/a.bak</span> = that one file. No trailing{' '}
                <span className="font-mono">/</span>.
              </p>
            </div>
          </div>

          <div className="mt-3">
            <label className={labelCls}>Permissions</label>
            <div className="flex flex-wrap gap-2">
              {permKeys.map((k) => {
                const on = (perms & Perm[k]) !== 0;
                return (
                  <label
                    key={k}
                    className={`flex cursor-pointer items-center gap-1.5 rounded border px-2.5 py-1 text-sm ${
                      on ? 'border-navy bg-navy/5 font-medium text-navy' : 'border-gray-300 text-gray-600'
                    }`}
                  >
                    <input type="checkbox" checked={on} onChange={() => togglePerm(Perm[k])} />
                    {k}
                  </label>
                );
              })}
            </div>
            {isPublic ? (
              <p className="mt-2 text-xs text-amber-700">
                ⚠ Anyone can {(perms & Perm.READ) !== 0 ? 'read' : ''}
                {(perms & Perm.READ) !== 0 && (perms & Perm.LIST) !== 0 ? ' and ' : ''}
                {(perms & Perm.LIST) !== 0 ? 'list' : ''}{' '}
                {prefix ? <>the <span className="font-mono">{prefix}</span> path in</> : 'all of'}{' '}
                <span className="font-mono">{bucket}</span> with no sign-in.
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {create.isError ? <p className="mt-3 text-sm text-red-600">{getErrorMessage(create.error)}</p> : null}

      <div className="mt-4 flex gap-2">
        <button
          className="rounded bg-navy px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
          disabled={!canCreate || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? 'Creating…' : 'Create'}
        </button>
        <button className="rounded border border-gray-300 px-4 py-1.5 text-sm hover:bg-gray-50" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---- filtering / sorting / paging over the principals grid -----------------

interface AclFilters {
  path: string;
  perms: number;
  showRevoked: boolean;
  advanced: boolean;
  name: string;
  principal: string;
  type: 'all' | 'apikey' | 'oauth';
  bucket: string;
  createdFrom: string;
  createdTo: string;
  expiresFrom: string;
  expiresTo: string;
}

const EMPTY_FILTERS: AclFilters = {
  path: '',
  perms: 0,
  showRevoked: false,
  advanced: false,
  name: '',
  principal: '',
  type: 'all',
  bucket: '',
  createdFrom: '',
  createdTo: '',
  expiresFrom: '',
  expiresTo: '',
};

type SortKey = 'name' | 'principal_type' | 'created_utc' | 'expires_utc';

interface SortState {
  key: SortKey;
  dir: 1 | -1;
}

/** ISO timestamps compare lexicographically, so date-only bounds compare on the date part. */
function datePart(iso: string): string {
  return iso.slice(0, 10);
}

function entryMatches(e: AclEntry, f: AclFilters): boolean {
  if (!f.showRevoked && e.revoked) return false;

  const grants = e.grants ?? [];
  const path = f.path.trim().toLowerCase();
  if (path && !grants.some((g) => (g.path_prefix || '(whole bucket)').toLowerCase().includes(path))) return false;
  if (f.perms !== 0 && !grants.some((g) => (g.perms & f.perms) === f.perms)) return false;

  if (f.advanced) {
    if (f.name.trim() && !e.name.toLowerCase().includes(f.name.trim().toLowerCase())) return false;
    if (f.type !== 'all' && e.principal_type !== f.type) return false;
    if (f.principal.trim()) {
      const q = f.principal.trim().toLowerCase();
      const hay = [e.oauth_email, e.oauth_subject, e.oauth_tenant, e.oauth_app_id, e.oauth_audience, e.key_prefix]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (f.bucket && !grants.some((g) => g.bucket === f.bucket)) return false;
    if (f.createdFrom && datePart(e.created_utc) < f.createdFrom) return false;
    if (f.createdTo && datePart(e.created_utc) > f.createdTo) return false;
    if (f.expiresFrom || f.expiresTo) {
      if (!e.expires_utc) return false;
      if (f.expiresFrom && datePart(e.expires_utc) < f.expiresFrom) return false;
      if (f.expiresTo && datePart(e.expires_utc) > f.expiresTo) return false;
    }
  }
  return true;
}

function compareEntries(a: AclEntry, b: AclEntry, sort: SortState): number {
  const av = a[sort.key] ?? null;
  const bv = b[sort.key] ?? null;
  // Missing values (e.g. no expiration) always sort last, regardless of direction.
  if (av === null && bv === null) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;
  return av < bv ? -sort.dir : av > bv ? sort.dir : 0;
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  sort: SortState;
  onSort: (key: SortKey) => void;
}) {
  const active = sort.key === sortKey;
  return (
    <th className="p-2">
      <button className={`flex items-center gap-1 hover:text-navy ${active ? 'text-navy' : ''}`} onClick={() => onSort(sortKey)}>
        {label}
        <span className="text-[10px]">{active ? (sort.dir === 1 ? '▲' : '▼') : ''}</span>
      </button>
    </th>
  );
}

/** Compact one-line summary of an entry's grants for the table row, e.g. 'releases/** · RL'. */
function grantSummary(e: AclEntry): string {
  const first = (e.grants ?? [])[0];
  if (!first) return '—';
  const perms = PERM_KEYS.filter((k) => (first.perms & Perm[k]) !== 0).map((k) => k.charAt(0)).join('');
  const path = first.path_prefix || '(whole bucket)';
  const rest = (e.grants ?? []).length - 1;
  return rest > 0 ? `${path} · ${perms} +${rest} more` : `${path} · ${perms}`;
}

export function Acl() {
  const qc = useQueryClient();
  const entries = useQuery({ queryKey: ['acl'], queryFn: () => api<{ entries: AclEntry[] }>('/api/admin/acl') });
  const buckets = useQuery({ queryKey: ['buckets'], queryFn: () => api<{ buckets: BucketInfo[] }>('/api/admin/buckets') });
  const publicGrants = useQuery({ queryKey: ['public'], queryFn: () => api<{ grants: PublicGrant[] }>('/api/admin/public') });
  const [showCreate, setShowCreate] = useState(false);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [filters, setFilters] = useState<AclFilters>(EMPTY_FILTERS);
  const [sort, setSort] = useState<SortState>({ key: 'created_utc', dir: -1 });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const revoke = useMutation({
    mutationFn: (id: string) => api(`/api/admin/acl/${id}/revoke`, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['acl'] }),
  });

  const removePublic = useMutation({
    mutationFn: (id: string) => api(`/api/admin/public/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['public'] }),
  });

  const setFilter = <K extends keyof AclFilters>(key: K, value: AclFilters[K]) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(0);
  };

  const onSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: 1 }));

  const all = entries.data?.entries ?? [];
  const filtered = useMemo(() => all.filter((e) => entryMatches(e, filters)).sort((a, b) => compareEntries(a, b, sort)), [all, filters, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const rows = filtered.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  const nowIso = new Date().toISOString();

  // 'Clear filters' shows when anything differs from the defaults (the advanced-panel
  // visibility itself is not a filter).
  const anyFilter = JSON.stringify({ ...filters, advanced: false }) !== JSON.stringify({ ...EMPTY_FILTERS, advanced: false });

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-bold text-navy">Access (ACL)</h1>
        <button
          className="rounded bg-navy px-3 py-1.5 text-sm font-semibold text-white"
          onClick={() => {
            setShowCreate(true);
            setNewToken(null);
          }}
        >
          New entry
        </button>
      </div>

      {newToken ? (
        <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm">
          <div className="font-semibold">Copy this API key now — it is shown only once:</div>
          <code className="mt-1 block break-all rounded bg-white p-2">{newToken}</code>
        </div>
      ) : null}

      {showCreate ? (
        <CreateForm
          buckets={buckets.data?.buckets ?? []}
          onClose={() => setShowCreate(false)}
          onCreated={(result) => {
            setShowCreate(false);
            setNewToken(result.token ?? null);
            void qc.invalidateQueries({ queryKey: ['acl'] });
            void qc.invalidateQueries({ queryKey: ['public'] });
          }}
        />
      ) : null}

      <h2 className="mb-2 text-sm font-semibold text-gray-700">Principals</h2>

      <div className="mb-2 flex flex-wrap items-center gap-3">
        <input
          className="w-64 rounded border border-gray-300 px-2.5 py-1.5 font-mono text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
          value={filters.path}
          onChange={(e) => setFilter('path', e.target.value)}
          placeholder="Search by path…"
          spellCheck={false}
        />
        <div className="flex gap-1.5">
          {PERM_KEYS.map((k) => {
            const on = (filters.perms & Perm[k]) !== 0;
            return (
              <button
                key={k}
                className={`rounded border px-2 py-1 text-xs ${on ? 'border-navy bg-navy/5 font-medium text-navy' : 'border-gray-300 text-gray-600'}`}
                onClick={() => setFilter('perms', on ? filters.perms & ~Perm[k] : filters.perms | Perm[k])}
              >
                {k}
              </button>
            );
          })}
        </div>
        <label className="flex cursor-pointer items-center gap-1.5 text-sm text-gray-600">
          <input type="checkbox" checked={filters.showRevoked} onChange={(e) => setFilter('showRevoked', e.target.checked)} />
          Show revoked
        </label>
        <button
          className={`text-sm hover:underline ${filters.advanced ? 'font-medium text-navy' : 'text-gray-600'}`}
          onClick={() => setFilter('advanced', !filters.advanced)}
        >
          Advanced {filters.advanced ? '▴' : '▾'}
        </button>
        {anyFilter ? (
          <button className="text-sm text-gray-500 hover:underline" onClick={() => setFilters(EMPTY_FILTERS)}>
            Clear filters
          </button>
        ) : null}
        <span className="ml-auto text-xs text-gray-500">
          {filtered.length} of {all.length} entr{all.length === 1 ? 'y' : 'ies'}
        </span>
      </div>

      {filters.advanced ? (
        <div className="mb-3 rounded-md border border-gray-200 bg-white p-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className={labelCls}>Name contains</label>
              <input className={inputCls} value={filters.name} onChange={(e) => setFilter('name', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Principal contains</label>
              <input
                className={inputCls}
                value={filters.principal}
                onChange={(e) => setFilter('principal', e.target.value)}
                placeholder="email, subject, tenant, app, audience…"
              />
            </div>
            <div>
              <label className={labelCls}>Type</label>
              <select className={inputCls} value={filters.type} onChange={(e) => setFilter('type', e.target.value as AclFilters['type'])}>
                <option value="all">All</option>
                <option value="apikey">API key</option>
                <option value="oauth">OAuth</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Bucket</label>
              <select className={inputCls} value={filters.bucket} onChange={(e) => setFilter('bucket', e.target.value)}>
                <option value="">All</option>
                {(buckets.data?.buckets ?? []).map((b) => (
                  <option key={b.logicalName} value={b.logicalName}>{b.logicalName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Created from</label>
              <input type="date" className={inputCls} value={filters.createdFrom} onChange={(e) => setFilter('createdFrom', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Created to</label>
              <input type="date" className={inputCls} value={filters.createdTo} onChange={(e) => setFilter('createdTo', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Expires from</label>
              <input type="date" className={inputCls} value={filters.expiresFrom} onChange={(e) => setFilter('expiresFrom', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Expires to</label>
              <input type="date" className={inputCls} value={filters.expiresTo} onChange={(e) => setFilter('expiresTo', e.target.value)} />
            </div>
          </div>
        </div>
      ) : null}

      <div className="overflow-hidden rounded border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50 text-left text-xs text-gray-500">
              <SortHeader label="Name" sortKey="name" sort={sort} onSort={onSort} />
              <SortHeader label="Type" sortKey="principal_type" sort={sort} onSort={onSort} />
              <th className="p-2">Principal</th>
              <th className="p-2">Access</th>
              <SortHeader label="Created" sortKey="created_utc" sort={sort} onSort={onSort} />
              <SortHeader label="Expires" sortKey="expires_utc" sort={sort} onSort={onSort} />
              <th className="p-2">Status</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => {
              const expired = !e.revoked && !!e.expires_utc && e.expires_utc <= nowIso;
              const selected = selectedId === e.id;
              return (
                <Fragment key={e.id}>
                  <tr
                    className={`cursor-pointer border-b hover:bg-gray-50 ${selected ? 'bg-gray-50' : ''}`}
                    onClick={() => setSelectedId(selected ? null : e.id)}
                  >
                    <td className="p-2">{e.name}</td>
                    <td className="p-2">{e.principal_type}</td>
                    <td className="p-2 text-xs text-gray-600">
                      {e.principal_type === 'apikey'
                        ? `${e.key_prefix ?? ''}…`
                        : e.oauth_email ?? e.oauth_subject ?? (e.oauth_tenant ? `tenant ${e.oauth_tenant}` : '')}
                      {e.oauth_app_id ? <span className="ml-1 text-gray-400">· app {e.oauth_app_id}</span> : null}
                      {e.oauth_audience ? <span className="ml-1 text-gray-400">· aud {e.oauth_audience}</span> : null}
                    </td>
                    <td className="p-2 font-mono text-xs text-gray-600">{grantSummary(e)}</td>
                    <td className="p-2 text-xs text-gray-600">{new Date(e.created_utc).toLocaleDateString()}</td>
                    <td className="p-2 text-xs text-gray-600">
                      {e.expires_utc ? new Date(e.expires_utc).toLocaleDateString() : '—'}
                    </td>
                    <td className="p-2">
                      {e.revoked ? (
                        <span className="text-red-600">revoked</span>
                      ) : expired ? (
                        <span className="text-amber-600">expired</span>
                      ) : (
                        <span className="text-green-700">active</span>
                      )}
                    </td>
                    <td className="p-2 text-right">
                      {!e.revoked ? (
                        <button
                          className="text-red-600 hover:underline"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            revoke.mutate(e.id);
                          }}
                        >
                          Revoke
                        </button>
                      ) : null}
                    </td>
                  </tr>
                  {selected ? (
                    <tr className="border-b">
                      <td colSpan={8} className="p-0">
                        <AclEntryDetails entry={e} onClose={() => setSelectedId(null)} />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
            {entries.data && filtered.length === 0 ? (
              <tr>
                <td className="p-3 text-sm text-gray-500" colSpan={8}>
                  {all.length === 0 ? 'No principals yet.' : 'No entries match the current filters.'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>

        {filtered.length > 0 ? (
          <div className="flex items-center justify-between border-t bg-gray-50 px-3 py-2 text-xs text-gray-600">
            <div className="flex items-center gap-2">
              <span>Rows per page</span>
              <select
                className="rounded border border-gray-300 px-1.5 py-0.5"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(0);
                }}
              >
                {[10, 25, 50].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <span>
                {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, filtered.length)} of {filtered.length}
              </span>
              <button
                className="rounded border border-gray-300 px-2 py-0.5 disabled:opacity-40"
                disabled={currentPage === 0}
                onClick={() => setPage(currentPage - 1)}
              >
                ‹ Prev
              </button>
              <button
                className="rounded border border-gray-300 px-2 py-0.5 disabled:opacity-40"
                disabled={currentPage >= pageCount - 1}
                onClick={() => setPage(currentPage + 1)}
              >
                Next ›
              </button>
            </div>
          </div>
        ) : null}

        {entries.isLoading ? <div className="p-3 text-sm">Loading…</div> : null}
        {entries.isError ? <div className="p-3 text-sm text-red-600">{getErrorMessage(entries.error)}</div> : null}
      </div>

      <h2 className="mb-2 mt-6 text-sm font-semibold text-gray-700">Public access (anonymous)</h2>
      <div className="overflow-hidden rounded border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50 text-left text-xs text-gray-500">
              <th className="p-2">Bucket</th>
              <th className="p-2">Path</th>
              <th className="p-2">Access</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {publicGrants.data?.grants.map((g) => (
              <tr key={g.id} className="border-b">
                <td className="p-2 font-mono">{g.bucket}</td>
                <td className="p-2 font-mono text-gray-600">{g.path_prefix || '(whole bucket)'}</td>
                <td className="p-2 text-xs text-gray-600">{permLabel(g.perms)}</td>
                <td className="p-2 text-right">
                  <button className="text-red-600 hover:underline" onClick={() => removePublic.mutate(g.id)}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {publicGrants.data && publicGrants.data.grants.length === 0 ? (
              <tr>
                <td className="p-3 text-sm text-gray-500" colSpan={4}>
                  Nothing is public — every object requires an API key or sign-in.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
        {publicGrants.isLoading ? <div className="p-3 text-sm">Loading…</div> : null}
        {publicGrants.isError ? <div className="p-3 text-sm text-red-600">{getErrorMessage(publicGrants.error)}</div> : null}
      </div>
    </div>
  );
}
